import React, { createContext, useContext, useReducer } from 'react';
import _ from 'lodash';
import { DateTime, Interval } from 'luxon';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { invalidateMostRelevantOrder, invalidateNextOrder, invalidateTimelines } from './invalidation-helpers';

export const OrderCalculatorContext = createContext();

export const StateProvider = ({ initialState = {}, children }) => (
  <OrderCalculatorContext.Provider value={useReducer(reducer, { ...defaults, ...initialState })}>
    {children}
  </OrderCalculatorContext.Provider>
);

export const useStateValue = () => useContext(OrderCalculatorContext);

const reducer = (state, action) => {
  switch (action.type) {
  case 'setSmoothingModel':
    return {
      ...state,
      smoothingModel: action.smoothingModel,
    };
  case 'selectHour':
    return {
      ...state,
      activeOrderId: undefined,
      activeOrder: {
        ...!state.activeOrderId && state.activeOrder, // reset all fields if switching from some existing order to a new order
        deliveryDate: action.selectedDate.toUTC().toISO(),
      },
      forecastedOrderVolume: action.forecastedOrderVolume,
      editingOrder: !state.activeOrderId && action.selectedDate >= DateTime.now() && state.editingOrder,
    };

  case 'isProducer':
    return { ...state, isProducer: action.isProducer };

  case 'setOrdersHref':
    return { ...state, ordersHref: action.ordersHref };

  case 'setOrders': {
    const orders =
      _.chain(action.orders)
        .orderBy('properties.deliveryDate')
        .filter(item => item.properties.state !== 'closed')
        .filter(item => !state.isProducer || !(item.properties.type === 'suggestion' && item.properties.state === 'ready'))
        .groupBy(i => DateTime.fromISO(i.properties.deliveryDate).toISODate())
        .value();
    const orderEntity = findOrder(state.activeOrderId, orders);
    return {
      ...state,
      orders,
      activeOrder: {},
      editingOrder: getDefaultEditState({ orderEntity, isProducer: state.isProducer }),
    };
  }

  case 'selectOrder': {
    const orderEntity = findOrder(action.activeOrderId, state.orders);
    return {
      ...state,
      ...orderEntity && selectDate({ state, date: DateTime.fromISO(orderEntity.properties.deliveryDate) }),
      activeOrderId: action.activeOrderId,
      activeOrder: {},
      editingOrder: getDefaultEditState({ orderEntity, isProducer: state.isProducer }),
    };
  }

  case 'editOrder':
    return { ...state, editingOrder: true };

  case 'cancelOrderEditing':
    return { ...state, editingOrder: false, activeOrder: {} };

  case 'deselectOrder':
    return { ...state, editingOrder: false, activeOrder: {}, activeOrderId: undefined };

  case 'setDeliveryDate': {
    const deliveryDate = DateTime.fromISO(action.deliveryDate);
    const newState = _.clone({ ...state, ...selectDate({ state, date: deliveryDate }) });
    // Ideally we shouldn't allow clearing deliveryDate. For now just ignoring it and returning unchanged state
    newState.activeOrder.deliveryDate = deliveryDate.toUTC().toISO() || state.activeOrder.deliveryDate;
    return newState;
  }

  case 'selectFeedId': {
    const newState = _.clone(state);
    newState.activeOrder.feedId = action.feedId;
    if (!action.feedId) {
      // Ideally we should not allow deselecting feedId once it's been selected, for now just setting quantity to 0 when it happens
      newState.activeOrder.orderQuantity = 0;
    }
    return newState;
  }

  case 'setOrderQuantity': {
    const newState = _.clone(state);
    newState.activeOrder.orderQuantity = action.systemOrderQuantity;
    return newState;
  }

  case 'setMedicated': {
    const newState = _.clone(state);
    newState.activeOrder.medicated = action.medicated;
    return newState;
  }

  case 'saveOrder':
  case 'cancelOrder':
    // noinspection JSIgnoredPromiseFromCall
    invalidateTimelines({ binEntity: action.binEntity });
    // noinspection JSIgnoredPromiseFromCall
    invalidateMostRelevantOrder({ binEntity: action.binEntity });
    // noinspection JSIgnoredPromiseFromCall
    invalidateNextOrder({ binEntity: action.binEntity });
    // noinspection JSIgnoredPromiseFromCall
    entityStore.invalidateHref(state.ordersHref);
    entityStore.clearExpiredItems(); // remove stale order forecasts
    return {
      ...state,
      activeOrderId: action.orderEntity?.getLink('self').href,
      activeOrder: {},
      editingOrder: false,
    };

  case 'setBinLevelTargetDate': {
    return {
      ...state,
      ...selectDate({ state, date: action.binLevelTargetDate }),
    };
  }

  case 'setBinLevelDayCount':
    const binLevelDayCount = Math.max(1, action.binLevelDayCount);
    const { binLevelDateRange } = state;
    binLevelDateRange.end = binLevelDateRange.start?.plus({ days: binLevelDayCount });
    return {
      ...state,
      binLevelDateRange,
      binLevelDayCount,
    };

  case 'moveBinLevelWindow': {
    const { days } = action;
    const start = state.binLevelDateRange.start.plus({ days }).startOf('day');
    const end = start.plus({ days: state.binLevelDayCount });
    return { ...state, binLevelDateRange: { start, end } };
  }

  case 'moveBinLevelWindowForward': {
    const start = state.binLevelDateRange.start.plus({ days: state.binLevelDayCount }).startOf('day');
    const end = start.plus({ days: state.binLevelDayCount });
    return { ...state, binLevelDateRange: { start, end } };
  }

  case 'moveBinLevelWindowBack': {
    const start = state.binLevelDateRange.start.plus({ days: -state.binLevelDayCount }).startOf('day');
    const end = start.plus({ days: state.binLevelDayCount });
    return { ...state, binLevelDateRange: { start, end } };
  }

  case 'setBinLevelData':
    return { ...state, binLevelData: action.binLevelData };

  default:
    throw new Error(`Unhandled state: ${action.type}`);
  }
};

function selectDate({ state, date }) {
  if (!date) {
    return {};
  }

  const { binLevelDateRange, binLevelDayCount } = state;
  const { start, end } = binLevelDateRange;

  if (!start || !end || !Interval.fromDateTimes(start, end).contains(date)) {
    const startOffset = Math.max(0, Math.floor(binLevelDayCount / 2) - 1);
    binLevelDateRange.start = date.minus({ days: startOffset }).startOf('day');
    binLevelDateRange.end = binLevelDateRange.start.plus({ days: binLevelDayCount });
  }

  return { binLevelDateRange, binLevelTargetDate: date };
}

const defaults = {
  editingOrder: false,        // Whether order is being edited
  isProducer: false,          // Whether the current user is a producer

  activeOrderId: undefined,   // the id of an order that has been selected in the calendar view
  activeOrder: {              // field values for the active order. Undefined means value is not overridden by order editor
    deliveryDate: undefined,
    orderQuantity: undefined,
    feedId: undefined,
    medicated: undefined,
  },

  binLevelTargetDate: undefined,  // used to update the calendar range to focus a specific date
  binLevelDayCount: 7,        // the number of days to display on the calender
  binLevelDateRange: {        // date range for showing bin level history+forecast
    start: undefined,
    end: undefined,
  },
  binLevelData: undefined,    // bin level history+forecast data grouped by day { 'yyyy-mm-dd': [] }

  ordersHref: undefined,      // href of the orders api endpoint
  orders: undefined,          // A formatted response of the orders API, grouped by deliveryDate

  smoothingModel: undefined,    // Whether showing estimates is currently enabled
};

function findOrder(orderId, orders) {
  return Object.values(orders || []).flat().find(entity => entity.getLink('self').href === orderId);
}

// default the order to `editing` mode when a mill views a request or a suggestion in the ready state
function getDefaultEditState({ orderEntity, isProducer }) {
  return !isProducer
    && orderEntity?.properties?.state === 'ready'
    && ['suggestion', 'request'].includes(orderEntity?.properties?.type);
}
